import React, { useMemo } from "react";
import styles from "../styles/deconstructedCard.module.css";

/**
 * DeconstructedCard
 * Shared visual shell that renders layered grid/shapes + frame, with children placed in content layer.
 * Usage: <DeconstructedCard className={someClass}><YourContent /></DeconstructedCard>
 */
const WAVE_PRESETS = {
  pink: {
    base: ["#FF6B6B", "#FF8E8E"],
    wave1: ["#FF7E7E", "#FFA0A0", "#FF7E7E"],
    wave2: ["#FFA0A0", "#FFBCBC", "#FFA0A0"],
    fill3: "#FFD4D4",
  },
  teal: {
    base: ["#4ECDC4", "#7EE8E1"],
    wave1: ["#5DDED5", "#8CEEEA", "#5DDED5"],
    wave2: ["#8CEEEA", "#BCF6F4", "#8CEEEA"],
    fill3: "#D6F9F7",
  },
  blue: {
    base: ["#118AB2", "#5AC8E3"],
    wave1: ["#2B97BC", "#73D5EC", "#2B97BC"],
    wave2: ["#73D5EC", "#9DE4F4", "#73D5EC"],
    fill3: "#C0EDF7",
  },
  navy: {
    base: ["#073B4C", "#3C7C8C"],
    wave1: ["#185064", "#4A8A9A", "#185064"],
    wave2: ["#4A8A9A", "#7AADBF", "#4A8A9A"],
    fill3: "#A0C5D1",
  },
};

const DeconstructedCard = ({ className = "", children, variant = "text", theme = "pink" }) => {
  const palette = useMemo(() => WAVE_PRESETS[theme] || WAVE_PRESETS.pink, [theme]);

  return (
    <article className={`${styles.deconstructedCard} ${className}`}>
      {variant === "image" ? (
        <div className={`${styles.cardLayer} ${styles.cardImage}`} aria-hidden>
          <svg className={styles.waveSvg} viewBox="0 0 300 400" preserveAspectRatio="none">
            <defs>
              <linearGradient id={`base-${theme}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={palette.base[0]} />
                <stop offset="100%" stopColor={palette.base[1]} />
              </linearGradient>
              <linearGradient id={`w1-${theme}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={palette.wave1[0]} />
                <stop offset="50%" stopColor={palette.wave1[1]} />
                <stop offset="100%" stopColor={palette.wave1[2]} />
              </linearGradient>
              <linearGradient id={`w2-${theme}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={palette.wave2[0]} />
                <stop offset="50%" stopColor={palette.wave2[1]} />
                <stop offset="100%" stopColor={palette.wave2[2]} />
              </linearGradient>
            </defs>
            <rect width="100%" height="100%" fill={`url(#base-${theme})`} />
            <path d="M0,230 C30,220 60,240 90,230 C120,220 150,240 180,230 C210,220 240,240 270,230 C290,225 295,230 300,225 L300,400 L0,400 Z" fill={`url(#w1-${theme})`} opacity="0.8" />
            <path d="M0,260 C40,250 80,270 120,260 C160,250 200,270 240,260 C280,250 290,260 300,255 L300,400 L0,400 Z" fill={`url(#w2-${theme})`} opacity="0.9" />
            <path d="M0,290 C50,280 100,300 150,290 C200,280 250,300 300,290 L300,400 L0,400 Z" fill={palette.fill3} opacity="0.7" />
          </svg>
        </div>
      ) : (
        <div className={`${styles.cardLayer} ${styles.cardBackground}`} aria-hidden>
          <div className={styles.bgGrid}>
            <div className={`${styles.gridLine} ${styles.horizontal}`} style={{ top: "25%" }} />
            <div className={`${styles.gridLine} ${styles.horizontal}`} style={{ top: "50%" }} />
            <div className={`${styles.gridLine} ${styles.horizontal}`} style={{ top: "75%" }} />
            <div className={`${styles.gridLine} ${styles.vertical}`} style={{ left: "33.33%" }} />
            <div className={`${styles.gridLine} ${styles.vertical}`} style={{ left: "66.66%" }} />
          </div>
          <div className={styles.bgObjects}>
            <div className={`${styles.bgObject} ${styles.circle}`} />
            <div className={`${styles.bgObject} ${styles.square}`} />
            <div className={`${styles.bgObject} ${styles.triangle}`} />
          </div>
        </div>
      )}

      {/* Frame layer */}
      <div className={`${styles.cardLayer} ${styles.cardFrame}`} aria-hidden>
        <svg viewBox="0 0 300 400" preserveAspectRatio="none" width="100%" height="100%">
          <path className={styles.framePath} d="M 20,20 H 280 V 380 H 20 Z" />
        </svg>
      </div>

      {/* Content layer (children) */}
      <div className={`${styles.cardLayer} ${styles.cardContent}`}>{children}</div>
    </article>
  );
};

export default DeconstructedCard;
